import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { API } from "../../../config/api.js";
import { get, patch } from "../../../lib/http.js";
import { AD } from "../../adminUi.js";
import {
  findRejectableField,
  normalizeApplication,
  parseRejectedFieldsFromRemark,
  REJECTABLE_FIELDS,
  safeDocHref,
  storedRole,
} from "../../../organizer/organizerApplication.js";

function pendingList(response) {
  if (Array.isArray(response)) return response;
  return (
    response?.applications ||
    response?.items ||
    response?.requests ||
    response?.data ||
    []
  );
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

function statusTone(status) {
  const key = String(status || "pending").toLowerCase();
  if (key === "approved") {
    return { label: "Approved", cls: "bg-emerald-100 text-emerald-800" };
  }
  if (key === "rejected") {
    return { label: "Rejected", cls: "bg-rose-100 text-rose-800" };
  }
  return { label: "Pending", cls: "bg-amber-100 text-amber-900" };
}

function DocViewButton({ label, url }) {
  const href = safeDocHref(url);

  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
      <span className="text-sm font-semibold text-slate-800">{label}</span>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-amber-600"
        >
          View
        </a>
      ) : (
        <span className="text-xs font-medium text-slate-400">Not uploaded</span>
      )}
    </div>
  );
}

/** Remark textarea with @field autocomplete for tagging rejected fields. */
function RemarkWithMentions({ value, onChange, autoFocus }) {
  const textareaRef = useRef(null);
  const [mention, setMention] = useState(null); // { start, query }

  const suggestions = useMemo(() => {
    if (!mention) return [];
    return findRejectableField(mention.query).slice(0, 8);
  }, [mention]);

  const detectMention = (text, caret) => {
    const before = text.slice(0, caret);
    const match = before.match(/@([A-Za-z0-9_]*)$/);
    if (!match) {
      setMention(null);
      return;
    }
    setMention({ start: caret - match[0].length, query: match[1] || "" });
  };

  const insertField = (field) => {
    const el = textareaRef.current;
    if (!el || !mention) return;
    const caret = el.selectionStart ?? value.length;
    const before = value.slice(0, mention.start);
    const after = value.slice(caret);
    const next = `${before}@${field.token} ${after}`;
    onChange(next);
    setMention(null);
    requestAnimationFrame(() => {
      const pos = before.length + field.token.length + 2;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const tagged = useMemo(() => parseRejectedFieldsFromRemark(value), [value]);

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(event) => {
          const next = event.target.value;
          onChange(next);
          detectMention(next, event.target.selectionStart);
        }}
        onKeyUp={(event) => detectMention(value, event.target.selectionStart)}
        onClick={(event) => detectMention(value, event.target.selectionStart)}
        onBlur={() => {
          // Delay so suggestion click can register
          setTimeout(() => setMention(null), 150);
        }}
        rows={4}
        autoFocus={autoFocus}
        required
        className="mt-2 w-full rounded-xl border border-slate-300 p-3 font-normal outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
        placeholder="Type remark… use @ to tag fields, e.g. Invalid @PANCard document"
      />

      {mention && suggestions.length > 0 ? (
        <div className="absolute left-0 right-0 z-20 mt-1 max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl">
          {suggestions.map((field) => (
            <button
              key={field.key}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => insertField(field)}
              className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-amber-50"
            >
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold text-amber-900">
                @{field.token}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-900">{field.label}</span>
                <span className="block text-[11px] text-slate-500">Step {field.step}</span>
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {tagged.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {tagged.map((key) => {
            const field = REJECTABLE_FIELDS.find((f) => f.key === key);
            return (
              <span
                key={key}
                className="inline-flex rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-bold text-rose-800"
              >
                {field?.label || key}
              </span>
            );
          })}
        </div>
      ) : (
        <p className="mt-2 text-xs text-slate-500">
          Tip: type <kbd className="rounded bg-slate-100 px-1">@</kbd> to tag fields that will highlight in red for the organizer.
        </p>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-slate-100 py-2.5 last:border-0 sm:grid-cols-[9rem_1fr] sm:gap-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="break-words text-sm text-slate-900">{value}</dd>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-amber-800">{title}</h3>
      <dl>{children}</dl>
    </section>
  );
}

export default function AdminOrganizerApplications() {
  const isSuperadmin = storedRole() === "superadmin";
  const [apps, setApps] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState("");
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [inspecting, setInspecting] = useState(null);
  const [inspectLoading, setInspectLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = statusFilter === "all" ? "?status=all" : `?status=${statusFilter}`;
      setApps(pendingList(await get(`${API.organizerApplication.pending}${query}`)));
    } catch (error) {
      toast.error(error.message || "Could not load organizer applications.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (isSuperadmin) load();
  }, [isSuperadmin, load]);

  const openInspect = async (item) => {
    const id = item._id || item.id;
    setInspecting(item);
    setInspectLoading(true);
    try {
      const response = await get(API.organizerApplication.byId(id));
      setInspecting(normalizeApplication(response) || item);
    } catch (error) {
      toast.error(error.message || "Could not load application details.");
    } finally {
      setInspectLoading(false);
    }
  };

  const approve = async (item) => {
    const id = item._id || item.id;
    setActingId(id);
    try {
      const response = await patch(API.organizerApplication.approve(id), {});
      const updated = normalizeApplication(response?.application) || {
        ...item,
        status: "approved",
        reviewedAt: new Date().toISOString(),
      };
      setApps((current) =>
        current.map((row) => ((row._id || row.id) === id ? { ...row, ...updated } : row))
      );
      if ((inspecting?._id || inspecting?.id) === id) {
        setInspecting((current) => (current ? { ...current, ...updated } : current));
      }
      const newId = response?.organizerId || response?.application?.organizerId;
      toast.success(
        `Application for “${item.legalName || item.contact?.fullName || "organizer"}” approved${
          newId ? ` · ID ${newId}` : ""
        }.`
      );
    } catch (error) {
      toast.error(error.message || "Approval failed.");
    } finally {
      setActingId("");
    }
  };

  const reject = async () => {
    const cleanReason = reason.trim();
    if (!cleanReason) {
      toast.warn("Please enter a remark explaining why this application is rejected.");
      return;
    }
    const rejectedFields = parseRejectedFieldsFromRemark(cleanReason);
    const id = rejecting._id || rejecting.id;
    setActingId(id);
    try {
      const response = await patch(API.organizerApplication.reject(id), {
        rejectionReason: cleanReason,
        rejectedFields,
      });
      const updated = normalizeApplication(response?.application) || {
        ...rejecting,
        status: "rejected",
        rejectionReason: cleanReason,
        rejectedFields,
        reviewedAt: new Date().toISOString(),
      };
      setApps((current) =>
        current.map((row) => ((row._id || row.id) === id ? { ...row, ...updated } : row))
      );
      if ((inspecting?._id || inspecting?.id) === id) {
        setInspecting((current) => (current ? { ...current, ...updated } : current));
      }
      setRejecting(null);
      setReason("");
      toast.success(
        rejectedFields.length
          ? `Rejected. ${rejectedFields.length} field(s) flagged for the organizer.`
          : "Application rejected. Remark shared with the organizer."
      );
    } catch (error) {
      toast.error(error.message || "Rejection failed.");
    } finally {
      setActingId("");
    }
  };

  if (!isSuperadmin) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-950">
        <h1 className="text-xl font-bold">Superadmin access required</h1>
        <p className="mt-2 text-sm">Only superadmins can review organizer applications.</p>
        <Link to="/admin" className="mt-4 inline-flex font-semibold text-amber-800 underline">
          Back to dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className={AD.page}>
      <div className={AD.headerRow}>
        <div>
          <h1 className={AD.title}>Organizer Applications</h1>
          <p className={AD.subtitle}>
            Review organizer verification requests and keep approved/rejected history visible here.
          </p>
        </div>
        <button type="button" onClick={load} className={AD.btnSecondary}>
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((option) => {
          const active = statusFilter === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setStatusFilter(option.value)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                active
                  ? "bg-amber-400 text-slate-950 shadow-sm"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-amber-50"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className={AD.tableShell}>
          <div className="space-y-3 p-4">
            {[0, 1, 2, 3, 4].map((item) => (
              <div key={item} className="h-12 animate-pulse rounded-xl bg-slate-100" />
            ))}
          </div>
        </div>
      ) : apps.length === 0 ? (
        <div className={AD.emptyState}>
          <div className="text-3xl">✓</div>
          <h2 className="mt-2 font-bold text-slate-900">Queue is clear</h2>
          <p className="mt-1 text-sm text-slate-500">
            No organizer applications found for this filter.
          </p>
        </div>
      ) : (
        <div className={AD.tableShell}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className={AD.thead}>
                <tr>
                  <th className="whitespace-nowrap px-4 py-3">Organisation</th>
                  <th className="whitespace-nowrap px-4 py-3">Contact</th>
                  <th className="whitespace-nowrap px-4 py-3">Entity</th>
                  <th className="whitespace-nowrap px-4 py-3">Bank</th>
                  <th className="whitespace-nowrap px-4 py-3">Submitted</th>
                  <th className="whitespace-nowrap px-4 py-3">Status</th>
                  <th className="whitespace-nowrap px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {apps.map((item) => {
                  const id = item._id || item.id;
                  const user = item.userId || item.user || item.createdBy;
                  const busy = actingId === id;
                  const tone = statusTone(item.status);
                  const isPending = String(item.status || "").toLowerCase() === "pending";
                  return (
                    <tr key={id} className="bg-white transition hover:bg-amber-50/40">
                      <td className="max-w-[14rem] px-4 py-3.5 align-top">
                        <div className="truncate font-bold text-slate-900">
                          {item.legalName || "Unnamed organisation"}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-slate-500">
                          By {user?.name || user?.email || (typeof user === "string" ? user : "Member")}
                        </div>
                      </td>
                      <td className="max-w-[14rem] px-4 py-3.5 align-top">
                        <div className="truncate font-medium text-slate-800">
                          {item.contact?.fullName || "—"}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-slate-500">
                          {item.contact?.email || "—"}
                        </div>
                        <div className="truncate text-xs text-slate-500">
                          {item.contact?.mobile || "—"}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3.5 align-top capitalize text-slate-700">
                        {item.entityType || "—"}
                      </td>
                      <td className="max-w-[11rem] px-4 py-3.5 align-top">
                        <div className="truncate text-slate-800">{item.bank?.bankName || "—"}</div>
                        <div className="mt-0.5 truncate text-xs capitalize text-slate-500">
                          {item.bank?.accountType || "—"}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3.5 align-top text-xs text-slate-600">
                        <div>{formatDate(item.submittedAt || item.updatedAt || item.createdAt)}</div>
                        {item.reviewedAt ? (
                          <div className="mt-1 text-[11px] text-slate-400">
                            Reviewed {formatDate(item.reviewedAt)}
                          </div>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3.5 align-top">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${tone.cls}`}>
                          {tone.label}
                        </span>
                        {item.organizerId ? (
                          <div className="mt-1 text-[11px] font-medium text-slate-500">
                            ID {item.organizerId}
                          </div>
                        ) : null}
                        {item.rejectionReason ? (
                          <div className="mt-2 max-w-[16rem] text-[11px] leading-5 text-rose-700">
                            {item.rejectionReason}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3.5 align-top">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openInspect(item)}
                            className={AD.btnSecondary}
                          >
                            Inspect
                          </button>
                          {isPending ? (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setRejecting(item);
                                  setReason("");
                                }}
                                className={AD.btnDanger}
                              >
                                Reject
                              </button>
                              <button
                                type="button"
                                onClick={() => approve(item)}
                                disabled={busy}
                                className={`${AD.btnPrimary} disabled:opacity-50`}
                              >
                                {busy ? "Approving…" : "Approve"}
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Inspect — full application details */}
      {inspecting && (
        <div
          className="fixed inset-0 z-[200] grid place-items-center bg-slate-950/60 p-4"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setInspecting(null);
          }}
        >
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl sm:p-6">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-4">
              <div>
                {(() => {
                  const tone = statusTone(inspecting.status);
                  return (
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${tone.cls}`}>
                      {tone.label}
                    </span>
                  );
                })()}
                <h2 className="mt-2 text-xl font-bold text-slate-900">
                  {inspecting.legalName || "Organizer application"}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Full application details for review · Submitted{" "}
                  {formatDate(inspecting.submittedAt || inspecting.updatedAt || inspecting.createdAt)}
                </p>
              </div>
              <button type="button" onClick={() => setInspecting(null)} className={AD.btnGhost}>
                Close
              </button>
            </div>

            {inspectLoading ? (
              <div className="mt-6 space-y-3">
                <div className="h-28 animate-pulse rounded-xl bg-slate-100" />
                <div className="h-28 animate-pulse rounded-xl bg-slate-100" />
                <div className="h-28 animate-pulse rounded-xl bg-slate-100" />
              </div>
            ) : (
              <div className="mt-5 grid gap-4">
                <SectionCard title="Organisation details">
                  <DetailRow label="Organizer ID" value={inspecting.organizerId || inspecting.userId?.organizerId} />
                  <DetailRow label="Entity type" value={inspecting.entityType} />
                  <DetailRow label="Legal name" value={inspecting.legalName} />
                  <DetailRow label="PAN" value={inspecting.pan} />
                  <DetailRow label="Address" value={inspecting.organisationAddress} />
                  <DetailRow
                    label="GSTIN"
                    value={
                      inspecting.hasGstin
                        ? inspecting.gstin || "Yes (number missing)"
                        : inspecting.hasGstin === false
                          ? "No"
                          : "—"
                    }
                  />
                </SectionCard>

                <SectionCard title="Review status">
                  <DetailRow label="Status" value={statusTone(inspecting.status).label} />
                  <DetailRow label="Submitted" value={formatDate(inspecting.submittedAt || inspecting.createdAt)} />
                  <DetailRow label="Reviewed" value={formatDate(inspecting.reviewedAt)} />
                  <DetailRow
                    label="Reviewed by"
                    value={inspecting.reviewedBy?.name || inspecting.reviewedBy?.email}
                  />
                  <DetailRow label="Rejection remark" value={inspecting.rejectionReason} />
                </SectionCard>

                <SectionCard title="Contact person">
                  <DetailRow label="Full name" value={inspecting.contact?.fullName} />
                  <DetailRow label="Email" value={inspecting.contact?.email} />
                  <DetailRow label="Mobile" value={inspecting.contact?.mobile} />
                </SectionCard>

                <SectionCard title="Bank details">
                  <DetailRow label="Beneficiary" value={inspecting.bank?.beneficiaryName} />
                  <DetailRow label="Account type" value={inspecting.bank?.accountType} />
                  <DetailRow label="Bank" value={inspecting.bank?.bankName} />
                  <DetailRow label="Account no." value={inspecting.bank?.accountNumber} />
                  <DetailRow label="IFSC" value={inspecting.bank?.ifsc} />
                </SectionCard>

                <section className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                  <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-amber-800">
                    Uploaded documents
                  </h3>
                  <div className="grid gap-2">
                    <DocViewButton label="PAN card" url={inspecting.documents?.panCardUrl} />
                    <DocViewButton
                      label="Cancelled cheque"
                      url={inspecting.documents?.cancelledChequeUrl}
                    />
                  </div>
                </section>
              </div>
            )}

            {String(inspecting.status || "").toLowerCase() === "pending" ? (
              <div className="mt-6 flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setRejecting(inspecting);
                    setReason("");
                  }}
                  className={AD.btnDanger}
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={() => approve(inspecting)}
                  disabled={actingId === (inspecting._id || inspecting.id)}
                  className={`${AD.btnPrimary} disabled:opacity-50`}
                >
                  {actingId === (inspecting._id || inspecting.id) ? "Approving…" : "Approve"}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Reject — remark required */}
      {rejecting && (
        <div
          className="fixed inset-0 z-[210] grid place-items-center bg-slate-950/60 p-4"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setRejecting(null);
              setReason("");
            }
          }}
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-xl font-bold text-slate-900">
              Reject “{rejecting.legalName || "application"}”?
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Add a remark and tag fields with <strong>@</strong> so those fields highlight in red for the organizer.
            </p>
            <label className="mt-5 block text-sm font-semibold text-slate-700">
              Rejection remark <span className="text-rose-500">*</span>
              <RemarkWithMentions value={reason} onChange={setReason} autoFocus />
            </label>
            <p className="mt-2 text-xs text-slate-500">
              Remark + tagged fields are shown on the organizer account setup page.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setRejecting(null);
                  setReason("");
                }}
                className={AD.btnSecondary}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={reject}
                disabled={!!actingId || !reason.trim()}
                className="rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {actingId ? "Submitting…" : "Submit rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
